import SearchIcon from "@mui/icons-material/Search";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { useEffect, useRef, useState, type JSX } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * The documentation search input. **URL-driven**: the query lives in `?q=`
 * on the page it sits on — the landing page, which shows results in place of
 * its sections while a query is typed — so a result set is bookmarkable and
 * survives a reload. Each keystroke REPLACES the entry (no history spam);
 * clearing the box drops the param. Any other param on the page is kept.
 *
 * THE BOX HOLDS ITS OWN TEXT, and follows the URL only when the URL changes
 * from somewhere else (Back, a pasted link). react-router 7 applies a
 * navigation inside `startTransition`, so an input whose value WAS the URL
 * re-rendered with the previous query between fast keystrokes and dropped
 * characters — typing "handbook" left "nk" (seen in `trainings.cy.ts`,
 * 2026-09-17). The URL is still written on every keystroke.
 */
export function DocSearchBox({
  autoFocus = false,
  placeholder = "Search articles, guides, FAQs and videos…",
  ariaLabel = "Search the documentation",
}: {
  autoFocus?: boolean;
  /** Trainings reuses this box over its own list (2026-09-17). */
  placeholder?: string;
  ariaLabel?: string;
}): JSX.Element {
  const [params, setParams] = useSearchParams();
  const urlValue = params.get("q") ?? "";
  const [value, setLocal] = useState(urlValue);
  // The last query THIS box wrote. A URL that differs from it came from
  // elsewhere, and the box follows it.
  const written = useRef(urlValue);

  useEffect(() => {
    if (urlValue.trim() !== written.current.trim()) {
      written.current = urlValue;
      setLocal(urlValue);
    }
  }, [urlValue]);

  const setValue = (next: string): void => {
    setLocal(next);
    written.current = next;
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next.trim()) out.set("q", next);
        else out.delete("q");
        return out;
      },
      { replace: true },
    );
  };

  return (
    <TextField
      fullWidth
      value={value}
      onChange={(e) => setValue(e.target.value)}
      autoFocus={autoFocus}
      placeholder={placeholder}
      inputProps={{ "aria-label": ariaLabel, "data-testid": "doc-search-input" }}
      InputProps={{
        sx: { fontSize: 15, bgcolor: "background.paper" },
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={() => setValue("")}
              sx={{ fontSize: 14, color: "text.disabled" }}
            >
              ✕
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
}
